import { useQueryClient, useMutation } from '@tanstack/react-query';
import { toBillableMaterial } from '../../../components/BillableMaterials/billableMaterial.helper';
import { BillableMaterial } from '../../../components/BillableMaterials/types';
import { useSnackbar } from '../../../components/SnackbarProvider';
import { BillableMaterialsService } from '../../generated';
import { billableMaterialsKeys } from '../../queries/billableMaterials';

interface UpdateBillableMaterialProps {
  organisationId: string;
  billableMaterialId: string;
  billableMaterial: Partial<BillableMaterial>;
}

export function useUpdateBillableMaterial() {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organisationId, billableMaterialId, billableMaterial }: UpdateBillableMaterialProps) => {
      const response = await BillableMaterialsService.billableMaterialControllerUpdateBillableMaterial({
        path: {
          organisationId,
          billableMaterialId,
        },
        body: {
          name: billableMaterial.name,
          article_number: billableMaterial.articleNumber,
          jobsite_ids: billableMaterial.jobsiteIds,
          condition_set: billableMaterial.conditionSets?.map((item) => ({ conditions: item.conditions })),
        },
      });

      if (response.data !== undefined) {
        return toBillableMaterial(response.data);
      } else {
        throw (
          (response as any).error ||
          new Error(response?.response?.statusText || 'Unknown error when updating billable material')
        );
      }
    },
    onSuccess: (updatedMaterial: BillableMaterial, variables: UpdateBillableMaterialProps) => {
      if (updatedMaterial) {
        queryClient.setQueryData<BillableMaterial[]>(
          billableMaterialsKeys.read({ organisationId: variables.organisationId }),
          (old) => old?.map((material) => (material.id === updatedMaterial.id ? updatedMaterial : material))
        );
      }
      showSnackbar('billableMaterial.saveSuccess', 'success');
    },
    onError: async () => {
      showSnackbar('billableMaterial.saveError', 'error');
    },
  });
}
